import '@/global.css'
import { Input } from "@/components/ui/input"
import { Link } from "react-router";
import { ROUTE } from '@/routes/constants'
import { useCallback, useState } from 'react'
import { Role, SCHOOLS } from '@/utils/constants'
import { registerStudent, registerTeacher, requestVerification, verifyRecaptcha } from '@/services/generatedAPIs'
import { generateSalt, generateVerificationToken, hashPassword } from '@/lib/crypto'
import { toast } from 'sonner'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { isEN } from '@/utils/i18n'
import ReCAPTCHA from "react-google-recaptcha";
import { useAsyncAction } from '@/hooks/use-async-action'
import { useTranslation } from 'react-i18next';

// 桌面端注册表单：按 Figma 873:108 / Frame 4 设计稿 1:1 还原。
// Frame 4 在 1920×1080 画布上的位置 = (1226, 179, 513, 707)，是一个可滚动视窗。
// 表单内容总高 ≈ 1080，超出 707 部分由 overflow-y:auto 实现滚动（设计稿亦带滚动条指示器）。
// 内部坐标统一相对 Frame 4 左上角（即 canvas 坐标 - 1226 / -179）。

// 共用样式：label / input / select 三种基本元素的视觉规范。
const labelStyle: React.CSSProperties = {
  position: 'absolute', left: 46, fontSize: 16, fontWeight: 400, color: '#575C60', lineHeight: '20px', whiteSpace: 'nowrap',
};
const inputBoxStyle: React.CSSProperties = {
  position: 'absolute', left: 142, width: 315, height: 43,
  borderRadius: 10, background: '#FFFFFF', border: 0,
  paddingLeft: 16, paddingRight: 16, fontSize: 14, color: '#1F1F1F',
};

// 注册页下拉框统一视觉：浅灰圆角弹层 + 胶囊高亮项 + 绿色滚动条（隐藏右侧 check 指示器）
// 仅在默认主题（html[data-theme=orange]）下生效；其他主题继续走 shadcn 默认。
const selectContentClass =
  "[html[data-theme=orange]_&]:bg-[#F3F3F4] [html[data-theme=orange]_&]:border-0 " +
  "[html[data-theme=orange]_&]:rounded-2xl [html[data-theme=orange]_&]:shadow-lg [html[data-theme=orange]_&]:p-2 " +
  "[html[data-theme=orange]_&]:[&::-webkit-scrollbar]:w-1.5 " +
  "[html[data-theme=orange]_&]:[&::-webkit-scrollbar-track]:bg-transparent " +
  "[html[data-theme=orange]_&]:[&::-webkit-scrollbar-thumb]:bg-[#9CEA4F] " +
  "[html[data-theme=orange]_&]:[&::-webkit-scrollbar-thumb]:rounded-full";
const selectItemClass =
  "[html[data-theme=orange]_&]:rounded-full [html[data-theme=orange]_&]:px-4 [html[data-theme=orange]_&]:py-2 " +
  "[html[data-theme=orange]_&]:text-sm [html[data-theme=orange]_&]:text-[#1F1F1F] " +
  "[html[data-theme=orange]_&]:data-[highlighted]:bg-[#E5E7EB] " +
  "[html[data-theme=orange]_&]:focus:bg-[#E5E7EB] " +
  "[html[data-theme=orange]_&]:[&>span:first-child]:hidden";

// 必填红星（label 末尾跟随）
function Star() {
  return <span style={{ color: '#E5484D', marginLeft: 2 }}>*</span>;
}

function SignupForm() {
  const { t, i18n } = useTranslation();
  const [signupMessage, setSignupMessage] = useState<string>("");
  const [role, setRole] = useState<string>(Role.STUDENT);
  const { isLoading, execute } = useAsyncAction({ showErrorToast: false });
  const [sex, setSex] = useState("X");
  const [email, setEmail] = useState<string>("");
  const [showRecaptcha, setShowRecaptcha] = useState<boolean>(false);
  const [schoolName, setSchoolName] = useState<string>("");

  function onSignup(formData: FormData) {
    if (formData.get("password") !== formData.get("confirm-password")) {
      setSignupMessage(t("user.confirm-password-mismatch"));
      return;
    }

    function onSignupSuccess() {
      alert(t("user.sign-up-success"));
      setSignupMessage(t("user.sign-up-success"));
    }
    function onSignupFailed() {
      setSignupMessage(t("user.sign-up-failed"));
    }

    execute(async () => {
      const salt = generateSalt();
      if (role === Role.TEACHER) {
        const res = await registerTeacher({
          email: formData.get("email") as string,
          name: formData.get("name") as string,
          nameEn: formData.get("enname") as string,
          password: hashPassword(formData.get("password") as string + salt),
          salt,
          schoolName,
          sex,
          captcha: formData.get("captcha") as string,
        });
        if (res.code === 200) onSignupSuccess(); else onSignupFailed();
      } else if (role === Role.STUDENT) {
        const res = await registerStudent({
          email: formData.get("email") as string,
          name: formData.get("name") as string,
          nameEn: formData.get("enname") as string,
          password: hashPassword(formData.get("password") as string + salt),
          salt,
          schoolName,
          classNumber: formData.has("classID") ? Number(formData.get("classID") as string) : undefined,
          schoolNumber: formData.has("schoolID") ? Number(formData.get("schoolID") as string) : undefined,
          gradeLevel: Number(formData.get("grade") as string),
          strn: '',
          sex,
          captcha: formData.get("captcha") as string,
        });
        if (res.code === 200) onSignupSuccess(); else onSignupFailed();
      }
    });
  }

  const onVerifyRecaptcha = useCallback(async (value: string | null) => {
    if (!value) return;
    try {
      const rsp = await verifyRecaptcha({ "g-recaptcha-response": value });
      if (rsp.code === 200 && rsp.data) {
        if (email) {
          const rsp2 = await requestVerification({ t: generateVerificationToken(email) });
          if (rsp2.code === 200) { toast(t('component.sendsuccess')); return; }
          toast(rsp2.message);
          return;
        }
      }
      toast(t('common.apierror'));
    } catch {
      toast(t('common.apierror'));
    } finally {
      setShowRecaptcha(false);
    }
  }, [email, t]);

  const isStudent = role === Role.STUDENT;
  // 教师模式没有 年級/班內編號/校內編號 三行（3×61=183px），其后字段整体上移这一段距离。
  const shift = isStudent ? 0 : 183;
  const rolePillStyle = (active: boolean): React.CSSProperties => ({
    position: 'absolute', top: 103, width: 137, height: 59, borderRadius: 28.5,
    background: active ? '#9CEA4F' : '#F3F3F4', color: active ? '#000' : '#575C60',
    border: 0, fontSize: 16, fontWeight: 500, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  });

  return (
    <>
      {showRecaptcha && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <ReCAPTCHA sitekey={import.meta.env.VITE_RECAPTCHA_SITEKEY} onChange={onVerifyRecaptcha} />
        </div>
      )}

      <form
        action={onSignup}
        style={{
          position: 'absolute', left: 1226, top: 179, width: 513, height: 707,
          overflowY: 'auto', overflowX: 'hidden',
        }}
      >
        {/* 表单内容容器：student=1080，teacher=897（学生独有 3 行被去除时，整体内容变短） */}
        <div style={{ position: 'relative', width: 513, height: 1080 - shift }}>
          {/* 标题「帳戶註冊」 */}
          <h2 style={{ position: 'absolute', left: 175, top: 0, margin: 0, fontSize: 32, fontWeight: 600, color: '#2A2F32', lineHeight: '40px', letterSpacing: '-0.01em' }}>
            {t('user.signup-title')}
          </h2>

          {/* 「請選擇身份：」 */}
          <div style={{ position: 'absolute', left: 46, top: 73, fontSize: 16, color: '#575C60', lineHeight: '20px' }}>
            {t('user.role-desc')}
          </div>

          {/* 學生 / 教師 胶囊按钮 */}
          <button type="button" onClick={() => setRole(Role.STUDENT)} style={{ ...rolePillStyle(isStudent), left: 46 }}>
            {t('user.student')}
          </button>
          <button type="button" onClick={() => setRole(Role.TEACHER)} style={{ ...rolePillStyle(!isStudent), left: 198 }}>
            {t('user.teacher')}
          </button>

          {signupMessage && (
            <p style={{ position: 'absolute', left: 46, top: 168, margin: 0, fontSize: 13, color: '#EF4444', lineHeight: '18px' }}>{signupMessage}</p>
          )}

          {/* 「請填寫你的個人資料：」 */}
          <div style={{ position: 'absolute', left: 46, top: 191, fontSize: 16, color: '#575C60', lineHeight: '20px' }}>
            {t('user.info-desc')}
          </div>

          {/* 姓名 */}
          <label htmlFor="name" style={{ ...labelStyle, top: 241 }}>{t('user.name')}<Star /></label>
          <Input id="name" name="name" type="text" required style={{ ...inputBoxStyle, top: 229 }} />

          {/* 英文名 */}
          <label htmlFor="enname" style={{ ...labelStyle, top: 302 }}>{t('user.enname')}<Star /></label>
          <Input id="enname" name="enname" type="text" required style={{ ...inputBoxStyle, top: 290 }} />

          {/* 性別 */}
          <label htmlFor="sex" style={{ ...labelStyle, top: 363 }}>{t('user.sex')}<Star /></label>
          <div style={{ position: 'absolute', left: 142, top: 351, width: 109, height: 43 }}>
            <Select name="sex" value={sex} onValueChange={setSex} required>
              <SelectTrigger style={{ width: 109, height: 43, borderRadius: 10, background: '#FFFFFF', border: 0, paddingLeft: 16, paddingRight: 16, fontSize: 14, color: '#1F1F1F' }}>
                <SelectValue id="sex" />
              </SelectTrigger>
              <SelectContent className={selectContentClass}>
                <SelectItem className={selectItemClass} value="X">{t('common.nottosay')}</SelectItem>
                <SelectItem className={selectItemClass} value="male">{t('common.male')}</SelectItem>
                <SelectItem className={selectItemClass} value="female">{t('common.female')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* 學校名稱 */}
          <label htmlFor="schoolname" style={{ ...labelStyle, top: 424 }}>{t('user.schoolname')}<Star /></label>
          <div style={{ position: 'absolute', left: 142, top: 412, width: 315, height: 43 }}>
            <Select name="schoolname" onValueChange={setSchoolName} value={schoolName} required>
              <SelectTrigger style={{ width: 315, height: 43, borderRadius: 10, background: '#FFFFFF', border: 0, paddingLeft: 16, paddingRight: 16, fontSize: 14, color: '#1F1F1F' }}>
                <SelectValue id="schoolname" />
              </SelectTrigger>
              <SelectContent className={selectContentClass}>
                <SelectGroup>
                  {SCHOOLS.map((school) => (
                    <SelectItem key={school.value} value={school.value}>
                      {isEN(i18n) ? school.en : school.zhHK}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* 學生独有：年級 / 班内編號 / 校内編號 */}
          {isStudent && (
            <>
              <label htmlFor="grade" style={{ ...labelStyle, top: 485 }}>{t('common.grade')}<Star /></label>
              <div style={{ position: 'absolute', left: 142, top: 473, width: 315, height: 43 }}>
                <Select name="grade" required>
                  <SelectTrigger style={{ width: 315, height: 43, borderRadius: 10, background: '#FFFFFF', border: 0, paddingLeft: 16, paddingRight: 16, fontSize: 14, color: '#1F1F1F' }}>
                    <SelectValue id="grade" />
                  </SelectTrigger>
                  <SelectContent className={selectContentClass}>
                    <SelectGroup>
                      <SelectItem className={selectItemClass} value="7">{t('teacher.s1')}</SelectItem>
                      <SelectItem className={selectItemClass} value="8">{t('teacher.s2')}</SelectItem>
                      <SelectItem className={selectItemClass} value="9">{t('teacher.s3')}</SelectItem>
                      <SelectItem className={selectItemClass} value="10">{t('teacher.s4')}</SelectItem>
                      <SelectItem className={selectItemClass} value="11">{t('teacher.s5')}</SelectItem>
                      <SelectItem className={selectItemClass} value="12">{t('teacher.s6')}</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <label htmlFor="classID" style={{ ...labelStyle, top: 546 }}>{t('common.classID')}</label>
              <Input id="classID" name="classID" type="number" style={{ ...inputBoxStyle, top: 534 }} />

              <label htmlFor="schoolID" style={{ ...labelStyle, top: 607 }}>{t('common.schoolID')}</label>
              <Input id="schoolID" name="schoolID" type="number" style={{ ...inputBoxStyle, top: 595 }} />
            </>
          )}

          {/* 電郵地址 + 獲取驗證碼（input 183 宽，按钮 114 宽）。教师模式 top 上移 shift */}
          <label htmlFor="email" style={{ ...labelStyle, top: 668 - shift }}>{t('user.email')}<Star /></label>
          <Input
            id="email" name="email" type="email" required placeholder="m@example.com"
            onInvalid={(e: any) => { e.target.setCustomValidity(t('user.email-error')); }}
            onInput={(e: any) => { e.target.setCustomValidity(""); setEmail(e.target.value); }}
            style={{ ...inputBoxStyle, left: 142, top: 656 - shift, width: 183 }}
          />
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setShowRecaptcha(true); }}
            style={{ position: 'absolute', left: 343, top: 656 - shift, width: 114, height: 43, borderRadius: 10, background: '#9CEA4F', color: '#000', border: 0, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
            className="hover:brightness-[0.97]"
          >
            {t('component.getcaptcha')}
          </button>

          {/* 驗證碼 */}
          <label htmlFor="captcha" style={{ ...labelStyle, top: 729 - shift }}>{t('user.captcha')}<Star /></label>
          <Input id="captcha" name="captcha" type="text" required style={{ ...inputBoxStyle, top: 717 - shift }} />

          {/* 密碼 */}
          <label htmlFor="password" style={{ ...labelStyle, top: 790 - shift }}>{t('user.password')}<Star /></label>
          <Input
            id="password" name="password" type="password" required
            pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$"
            onInvalid={(e: any) => { e.target.setCustomValidity(t('user.password-error')); }}
            onInput={(e: any) => { e.target.setCustomValidity(""); }}
            style={{ ...inputBoxStyle, top: 778 - shift }}
          />

          {/* 確認密碼 */}
          <label htmlFor="confirm-password" style={{ ...labelStyle, top: 851 - shift }}>{t('user.confirm-password')}<Star /></label>
          <Input
            id="confirm-password" name="confirm-password" type="password" required
            pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$"
            onInvalid={(e: any) => { e.target.setCustomValidity(t('user.password-error')); }}
            onInput={(e: any) => { e.target.setCustomValidity(""); }}
            style={{ ...inputBoxStyle, top: 839 - shift }}
          />

          {/* 協議文案 */}
          <div style={{ position: 'absolute', left: 79, top: 908 - shift, fontSize: 14, color: '#575C60', lineHeight: '24px', whiteSpace: 'nowrap' }}>
            {t('user.create-account-msg')}
            <Link to={ROUTE.PRIVACY_POLICY} style={{ marginLeft: 4, color: '#5BC9E8' }} className="hover:underline">
              {t('user.privacypolicy')}
            </Link>
          </div>

          {/* 創建賬戶按钮 */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              position: 'absolute', left: 46, top: 957 - shift, width: 421, height: 73, borderRadius: 36.5,
              background: '#5BC9E8', color: '#FFFFFF', border: 0,
              fontSize: 24, fontWeight: 500, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: isLoading ? 0.6 : 1,
            }}
            className="hover:brightness-[0.97]"
          >
            {t('user.create-account')}
          </button>

          {/* 已有帳戶 -> 登入 */}
          <div style={{ position: 'absolute', left: 161, top: 1056 - shift, fontSize: 14, color: '#575C60', lineHeight: '24px', whiteSpace: 'nowrap' }}>
            {t('user.signup-comment')}
            <Link to={ROUTE.LOGIN} style={{ marginLeft: 4, color: '#5BC9E8' }} className="hover:underline">
              {t('user.sign-in')}
            </Link>
          </div>
        </div>
      </form>
    </>
  );
}

export default SignupForm;
